import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Tabs, Tab, TextField, InputAdornment,
  CircularProgress, Alert
} from '@mui/material';
import {
  Search, Store, AutoAwesome, TrendingUp
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import DialogCloseButton from '../common/DialogCloseButton';
import VerifierMarketplaceTab from './VerifierMarketplaceTab';
import PreviousVerifiersTab from './PreviousVerifiersTab';
import AIAssistedHireDialog from '../hires/AIAssistedHireDialog';
import api from '../../services/api';
import { brand } from '../../theme/colors';

const FindVerifierDialog = ({ open, onClose, project, onVerifierSelected }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Marketplace verifiers
  const [marketplaceVerifiers, setMarketplaceVerifiers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVerifiers, setFilteredVerifiers] = useState([]);
  
  // AI recommendations
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  
  // Previous verifiers
  const [previousVerifiers, setPreviousVerifiers] = useState([]);
  
  // Hire dialog
  const [showHireDialog, setShowHireDialog] = useState(false);
  const [selectedVerifier, setSelectedVerifier] = useState(null);
  
  // Filters
  const getInitialFilters = () => ({
    tier: '',
    city: '',
    specializations: [],
    minRating: 0,
    maxPrice: null,
    sortBy: 'average_rating'
  });
  const [filters, setFilters] = useState(getInitialFilters);

  useEffect(() => {
    if (open) {
      setFilters(getInitialFilters());
      loadData();
    }
  }, [open, activeTab]);

  useEffect(() => {
    // Filter marketplace verifiers based on search
    if (searchTerm) {
      handleAISearch(searchTerm);
    } else {
      setFilteredVerifiers(marketplaceVerifiers);
    }
  }, [searchTerm, marketplaceVerifiers]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 0) {
        // Load marketplace verifiers
        await loadMarketplaceVerifiers();
      } else if (activeTab === 1) {
        // Load previous verifiers
        await loadPreviousVerifiers();
      }
    } catch (err) {
      console.error('Error loading verifiers:', err);
      setError(err.message || 'Failed to load verifiers');
    } finally {
      setLoading(false);
    }
  };

  const loadMarketplaceVerifiers = async (overrideFilters = null) => {
    const activeFilters = overrideFilters || filters;
    try {
      const params = {
        ...activeFilters,
        specializations: activeFilters.specializations.join(','),
        limit: 20
      };

      const response = await api.get('/verifiers', { params });
      const verifiers = response.data.data || response.data.verifiers || [];
      setMarketplaceVerifiers(verifiers);
      setFilteredVerifiers(verifiers);
    } catch (err) {
      console.error('Error loading marketplace verifiers:', err);
      setError('Failed to load verifiers. Please try again.');
      setMarketplaceVerifiers([]);
      setFilteredVerifiers([]);
    }
  };

  const loadPreviousVerifiers = async () => {
    try {
      const response = await api.get('/contractor/verifier-marketplace/previous');
      setPreviousVerifiers(response.data.data || []);
    } catch (err) {
      console.error('Error loading previous verifiers:', err);
      setPreviousVerifiers([]);
    }
  };

  const loadAIRecommendations = async () => {
    if (!project) {
      setError('Project information is required for AI recommendations');
      return;
    }
    
    setLoadingRecommendations(true);
    setError('');
    
    try {
      const response = await api.post('/contractor/verifier-marketplace/ai-recommendations', {
        projectId: project.id,
        projectType: project.type || project.category,
        projectLocation: project.location,
        projectBudget: project.budget,
        projectDescription: project.description,
        urgency: project.urgency || 'normal',
        limit: 5
      });
      
      setAiRecommendations(response.data.data || []);
      setShowRecommendations(true);
    } catch (err) {
      console.error('Error loading AI recommendations:', err);
      setError('Failed to generate AI recommendations. Please try again.');
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const handleAISearch = (query) => {
    if (!query || query.trim().length === 0) {
      setFilteredVerifiers(marketplaceVerifiers);
      return;
    }
    const q = query.toLowerCase();
    const filtered = marketplaceVerifiers.filter(v =>
      v.name?.toLowerCase().includes(q) ||
      v.company_name?.toLowerCase().includes(q) ||
      v.bio?.toLowerCase().includes(q) ||
      v.city?.toLowerCase().includes(q) ||
      v.specializations?.some(s => s.toLowerCase().includes(q))
    );
    setFilteredVerifiers(filtered);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm('');
    setError('');
    setShowRecommendations(false);
  };

  const handleHireClick = (verifier) => {
    setSelectedVerifier(verifier);
    setShowHireDialog(true);
  };

  const handleHireSuccess = () => {
    setShowHireDialog(false);
    setSelectedVerifier(null);
    if (onVerifierSelected) {
      onVerifierSelected(selectedVerifier);
    }
    // Optionally close the main dialog
    // onClose();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    loadMarketplaceVerifiers(newFilters);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogCloseButton onClick={onClose} />
        {/* Header with purple gradient */}
        <DialogTitle
          sx={{
            background: brand.gradient,
            color: 'white',
            py: 3,
            px: 3
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={700} letterSpacing={1}>
                <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                Find Verifier
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                Browse marketplace or hire from previous verifiers
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  minHeight: 44,
                  color: 'text.secondary'
                },
                '& .Mui-selected': {
                  color: 'text.primary !important',
                  fontWeight: 600
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'text.primary',
                  height: 2
                }
              }}
            >
              <Tab label="Marketplace" />
              <Tab label="Previously Hired" />
            </Tabs>
          </Box>

          {/* Search and AI Recommendations Bar */}
          {activeTab === 0 && (
            <Box sx={{ px: 3, py: 2, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search verifiers by name, specialization, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'white',
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Box sx={{ px: 3, pt: 2 }}>
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            </Box>
          )}

          {/* Tab Content */}
          <Box sx={{ p: 3, minHeight: 400 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {activeTab === 0 && (
                  <VerifierMarketplaceTab
                    verifiers={showRecommendations ? aiRecommendations : filteredVerifiers}
                    aiRecommendations={showRecommendations ? aiRecommendations : []}
                    onHireClick={handleHireClick}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    loading={loading}
                  />
                )}
                {activeTab === 1 && (
                  <PreviousVerifiersTab
                    verifiers={previousVerifiers}
                    onHireClick={handleHireClick}
                    loading={loading}
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI-Assisted Hire Verifier Dialog */}
      {showHireDialog && selectedVerifier && (
        <AIAssistedHireDialog
          open={showHireDialog}
          onClose={() => {
            setShowHireDialog(false);
            setSelectedVerifier(null);
          }}
          targetUser={selectedVerifier}
          userRole={selectedVerifier.tier === 'professional' ? 'verifier_professional' : 'verifier_general'}
          project={project || {
            id: null,
            title: 'New Project',
            name: 'New Project',
            type: 'Construction',
            location: 'Zimbabwe',
            description: ''
          }}
          onSuccess={(data) => {
            console.log('Hire request sent successfully:', data);
            handleHireSuccess();
          }}
        />
      )}
    </>
  );
};

export default FindVerifierDialog;
